import React from 'react';
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import LoadingIcon from '@/app/assets/icons/loading';
import ConsentDisclaimer from '@/app/components/shared/consent-disclaimer';

interface Question {
    Title: string;
    question: string;
    options: string[];
    qid: string;
}

interface QuestionInputProps {
    question: Question;
    onSubmit: (qid: string, selectedItems: string[]) => void;
    isMultiSelect?: boolean;
    isLastQuestion?: boolean;
    buttonLoader: boolean;
}

const QuestionsInput: React.FC<QuestionInputProps> = ({ 
    question, 
    onSubmit, 
    isMultiSelect = true,
    isLastQuestion,
    buttonLoader
}) => {
    const [selectedItems, setSelectedItems] = React.useState<string[]>([]);

    // Reset selections when question changes
    React.useEffect(() => {
        setSelectedItems([]);
    }, [question.qid]);

    const handleItemSelect = (option: string) => {
        if (isMultiSelect) {
            // Multi-select behavior
            setSelectedItems(prev => {
                const isSelected = prev.includes(option);
                if (isSelected) {
                    return prev.filter(item => item !== option);
                }
                return [...prev, option];
            });
        } else {
            // Single-select behavior
            setSelectedItems([option]);
        }
    };

    const handleSubmit = () => {
        onSubmit(question.qid, selectedItems);
    };

    return (
        <div className="flex flex-col items-start justify-between">
            <div className="mt-6 md:mt-0 flex flex-col items-start">
                <p className="text-4xl font-semibold leading-[41.6px] tracking-[-0.48px] mb-12 md:mb-8">{question.Title}</p>
                <p className="text-sm font-normal text-neutral-300 leading-5 mb-3">{question.question}*</p>
            </div>  

            <div className="flex gap-2 flex-wrap">
                {question.options.map((option) => (
                    <label 
                        key={option}
                        className={`text-sm leading-[22.4px] tracking-[0.14px] flex max-w-max font-medium items-center gap-2 py-s4 px-s6 border rounded-full cursor-pointer transition-colors duration-200 ${
                            selectedItems.includes(option) 
                                ? 'bg-brand-primary-100' 
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        <input
                            type={isMultiSelect ? "checkbox" : "radio"}
                            name={question.qid}
                            checked={selectedItems.includes(option)}
                            onChange={() => handleItemSelect(option)}
                            className="hidden"
                        />
                        <span className="flex-1">{option}</span>
                    </label>
                ))}
            </div>
            
            <RegularButton
                showRightIcon={false}
                noIcon={true}
                size={ButtonSize.Large}
                variant={ButtonVariant.PrimaryDefault}
                onClick={handleSubmit}
                disabled={selectedItems.length === 0 || buttonLoader}
                className="md:relative absolute bottom-8 md:bottom-auto md:mb-0 md:mt-8 text-lg transition-opacity duration-300 ease-in-out w-full md:min-w-0 min-w-[calc(100vw-48px)]"
                label={buttonLoader ?  <LoadingIcon className="m-auto animate-spin w-5 h-5" /> : isLastQuestion ? 'Done' : 'Next'}
            />
            <ConsentDisclaimer classnames="mt-8"/>
        </div>
    );
};

export default QuestionsInput;